import React from "react";
import {
  Form, Input, Button, Select, Space, Card, Typography,
  InputNumber, Row, Col, Tooltip, Tag, Table, Divider,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, LoadingOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import type { FormInstance } from "antd";
import { Booking } from "../../types/booking";
import {
  FormValues, InvoiceLine, PricingComponent,
  calculateTotal,
} from "./invoiceFormHelpers";
import { PaymentTermsSection } from "./form-sections";
import type { PricingPaymentTerm } from "./form-sections/PaymentTermsSection";

const { Text } = Typography;
const { Option } = Select;

interface Step4Props {
  form: FormInstance;
  selectedBooking: Booking | null;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  pricingComponents: PricingComponent[];
  isEditMode: boolean;
  /** True only when opening an existing invoice to edit — not a new draft. */
  isExistingInvoice?: boolean;
  loading: boolean;
  autoSaving: boolean;
  lastSaved: Date | null;
  savingLineIndex: number | null;
  autogeneratingLines: boolean;
  autoSaveInvoiceLine: (index: number, data: InvoiceLine) => void;
  autoSaveInvoice: (values: FormValues) => void;
  onCancel: () => void;
  onCompanyPaymentTermUpdated?: (
    term: PricingPaymentTerm | null,
  ) => void | Promise<void>;
}

const CALC_TYPE_LABELS: Record<string, string> = {
  per_carton: "Cartons", per_pallet: "Pallets", per_unique_product: "Products",
  per_kg: "KG", per_cbm: "CBM", per_shipment: "Shipment",
  percentage: "Pct", fixed: "Fixed",
};

const SPECIAL_COST_TYPES = [
  "tariff", "customs_btw", "customs_duties", "customs_commission", "customs_commission_btw", "customs_commission_duties",
  "lmd_delivery", "lmd_diesel_surcharge", "lmd_nl_pallet_surcharge", "europallet_non_exchange",
  "export_clearance", "pickup_fee",
  "commodity_inspection", "post_export_license", "pricing_component",
];

const InvoiceWizardStep4Review: React.FC<Step4Props> = (props) => {
  const {
    form, selectedBooking, formValues, setFormValues,
    pricingComponents, isEditMode, isExistingInvoice = false, loading,
    savingLineIndex, autoSaveInvoiceLine, autoSaveInvoice, onCancel,
    onCompanyPaymentTermUpdated,
  } = props;

  const saveLineOnBlur = (index: number) => {
    if (!isEditMode) return;
    const lines = form.getFieldValue("lines");
    if (lines?.[index]) autoSaveInvoiceLine(index, lines[index]);
  };

  const getCalcType = (line: InvoiceLine | undefined): string => {
    if (!line?.cost_type || SPECIAL_COST_TYPES.includes(line.cost_type)) return "";
    const comp = pricingComponents.find(
      (c) => c.id.toString() === line.cost_type?.toString() || c.name === line.cost_type
    );
    if (!comp) return "";
    if (comp.name?.toLowerCase().includes("import duties") ||
        comp.name?.toLowerCase().includes("invoerrechten") ||
        comp.name?.toLowerCase().includes("btw bij invoer") ||
        comp.name?.toLowerCase().includes("voorschotprovisie")) return "";
    return comp.calculation_type || "";
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (_: unknown, record: { name: number; index: number }) => {
        const line = (formValues.lines || [])[record.index];
        return (
          <div style={{ minWidth: 0, maxWidth: "100%" }}>
            <Form.Item name={[record.name, "description"]} rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 4 }}>
              <Input placeholder="e.g. Freight charges" size="small" onBlur={() => saveLineOnBlur(record.index)} />
            </Form.Item>
            {line?.template_source && <Tag color="blue" style={{ fontSize: "10px" }}>From {line.template_source}</Tag>}
            <Form.Item name={[record.name, "cost_type"]} style={{ marginBottom: 0 }}>
              <Select
                placeholder="Select cost type"
                size="small"
                style={{ width: "100%", fontSize: "10px" }}
                optionLabelProp="label"
                popupMatchSelectWidth={false}
                dropdownStyle={{ maxWidth: 420 }}
                onChange={(value) => {
                  let desc = "";
                  if (value === "export_clearance") desc = "Export Clearance License Fee";
                  else if (value === "pickup_fee") desc = "Pick-up Fee";
                  else if (value === "commodity_inspection") desc = "Commodity Inspection";
                  else if (value === "post_export_license") desc = "Post Export License";
                  else if (value === "custom") desc = "Custom Line Item";
                  else {
                    const comp = pricingComponents.find((c) => c.id.toString() === value);
                    if (comp) desc = comp.description || comp.name;
                  }
                  if (desc) {
                    const lines = form.getFieldValue("lines");
                    if (lines?.[record.index]) {
                      lines[record.index].description = desc;
                      lines[record.index].vat_percentage = 0;
                      form.setFieldsValue({ lines });
                      setFormValues((prev) => ({ ...prev, lines: [...lines] }));
                    }
                  }
                  if (isEditMode) setTimeout(() => saveLineOnBlur(record.index), 100);
                }}
              >
                {pricingComponents.map((c) => {
                  const calc =
                    c.calculation_type
                      ? CALC_TYPE_LABELS[c.calculation_type] || c.calculation_type
                      : "";
                  const shortLabel = calc ? `${c.name} · ${calc}` : c.name;
                  return (
                    <Option
                      key={c.id}
                      value={c.id.toString()}
                      label={shortLabel}
                      title={
                        c.description && c.description !== c.name
                          ? `${c.name} — ${c.description}`
                          : c.name
                      }
                    >
                      <div style={{ lineHeight: 1.3 }}>
                        <Text strong style={{ fontSize: 12 }}>
                          {c.name}
                          {calc ? ` · ${calc}` : ""}
                        </Text>
                        {c.description && c.description !== c.name && (
                          <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {c.description}
                            </Text>
                          </div>
                        )}
                      </div>
                    </Option>
                  );
                })}
                <Option key="export_clearance" value="export_clearance" label="Export Clearance License · Fixed">
                  <Text strong>Export Clearance License · Fixed</Text>
                </Option>
                <Option key="pickup_fee" value="pickup_fee" label="Pick-up Fee · Variable">
                  <Text strong>Pick-up Fee · Variable</Text>
                </Option>
                <Option key="commodity_inspection" value="commodity_inspection" label="Commodity Inspection · €75">
                  <Text strong>Commodity Inspection · €75</Text>
                </Option>
                <Option key="post_export_license" value="post_export_license" label="Post Export License · Variable">
                  <Text strong>Post Export License · Variable</Text>
                </Option>
                <Option key="custom" value="custom" label="Custom / Manual">
                  <Text strong>Custom / Manual</Text>
                </Option>
              </Select>
            </Form.Item>
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 88,
      render: (_: unknown, record: { name: number; index: number }) => {
        const line = (formValues.lines || [])[record.index];
        const calcType = getCalcType(line);
        return (
          <div>
            <Form.Item name={[record.name, "vat_percentage"]} hidden initialValue={0}>
              <InputNumber />
            </Form.Item>
            <Form.Item name={[record.name, "quantity"]} rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}>
              <InputNumber min={0} precision={3} size="small" style={{ width: "100%" }} onBlur={() => saveLineOnBlur(record.index)} />
            </Form.Item>
            {calcType && <Tag color="blue" style={{ fontSize: "8px", marginTop: 2 }}>{CALC_TYPE_LABELS[calcType] || calcType}</Tag>}
          </div>
        );
      },
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 110,
      render: (_: unknown, record: { name: number; index: number }) => (
        <Form.Item name={[record.name, "unit_price"]} rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}>
          <InputNumber min={0} step={0.01} precision={2} size="small" style={{ width: "100%" }}
            formatter={(v) => v ? `€${Number(v).toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "€0.00"}
            parser={(v) => parseFloat((v || "0").replace(/€\s?|(,*)/g, "")) as unknown as 0}
            onFocus={(e) => { const l = (formValues.lines || [])[record.index]; if (l?.unit_price === 0) e.target.select(); }}
            onBlur={() => saveLineOnBlur(record.index)}
          />
        </Form.Item>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 80,
      render: (_: unknown, record: { index: number }) => {
        const line = (formValues.lines || [])[record.index];
        const total = line ? (line.quantity || 0) * (line.unit_price || 0) : 0;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Text strong style={{ fontSize: "12px" }}>€{total.toFixed(2)}</Text>
            {savingLineIndex === record.index && <LoadingOutlined style={{ marginLeft: 4, fontSize: "10px" }} />}
          </div>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 40,
      render: (_: unknown, record: { name: number }, _idx: unknown, action: { fields: unknown[]; remove: (name: number) => void }) => (
        <Button danger size="small" icon={<DeleteOutlined />}
          onClick={() => {
            action.remove(record.name);
            if (isEditMode) {
              setTimeout(() => {
                const updated = { ...formValues, lines: form.getFieldValue("lines") || [] };
                setFormValues(updated);
                autoSaveInvoice(updated);
              }, 100);
            }
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PaymentTermsSection
        selectedBooking={selectedBooking}
        paymentTerm={formValues.payment_term}
        customPaymentTerms={formValues.custom_payment_terms}
        onCompanyPaymentTermUpdated={onCompanyPaymentTermUpdated}
      />
      {selectedBooking && (
        <>
          <Form.Item name="payment_term" hidden><Input type="hidden" /></Form.Item>
          <Form.Item name="custom_payment_terms" hidden><Input type="hidden" /></Form.Item>
        </>
      )}

      <Divider orientation="left" style={{ margin: "12px 0" }}>
        Invoice Lines
        {props.autogeneratingLines && <LoadingOutlined style={{ marginLeft: 8 }} />}
        <Tooltip title="Invoice lines are generated based on matching pricing templates.">
          <InfoCircleOutlined style={{ marginLeft: 8, cursor: "pointer" }} />
        </Tooltip>
      </Divider>

      <Form.List name="lines">
        {(fields, { add, remove }) => (
          <>
            <Table
              dataSource={fields.map((field, index) => ({ ...field, index }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 12 }}
              tableLayout="fixed"
              columns={columns.map((col) => {
                if (col.key === "action") {
                  return {
                    ...col,
                    render: (_: unknown, record: { name: number }) =>
                      fields.length > 1 && (
                        <Button danger size="small" icon={<DeleteOutlined />}
                          onClick={() => {
                            remove(record.name);
                            if (isEditMode) {
                              setTimeout(() => {
                                const updated = { ...formValues, lines: form.getFieldValue("lines") || [] };
                                setFormValues(updated);
                                autoSaveInvoice(updated);
                              }, 100);
                            }
                          }}
                        />
                      ),
                  };
                }
                return col;
              })}
            />
            <Form.Item style={{ marginBottom: 8 }}>
              <Button type="dashed" onClick={() => add({ description: "", quantity: 1, unit_price: 0, vat_percentage: 0, cost_type: "" })}
                icon={<PlusOutlined />} block size="small">
                Add Line Item
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Divider style={{ margin: "8px 0" }} />

      <Row justify="end">
        <Col>
          <Text strong>Total Amount: € {calculateTotal(formValues.lines || []).toFixed(2)}</Text>
        </Col>
      </Row>

      <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              {isExistingInvoice ? "Update Invoice" : "Create Invoice"}
            </Button>
            <Button onClick={onCancel} size="small">Cancel</Button>
          </Space>
          {isEditMode && <Text type="secondary" style={{ fontSize: "11px" }}>Changes are automatically saved as you edit</Text>}
        </Space>
      </Form.Item>
    </>
  );
};

export default InvoiceWizardStep4Review;
